<script lang="ts" setup>
import { computed, ref, StyleValue, watch, onMounted, onUnmounted, nextTick } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { HeaderList } from '@/coreDomainsModule/table';

const MENU_WIDTH = 200;
const MENU_OFFSET_Y = 6;
const MENU_MIN_HEIGHT = 300;

const props = withDefaults(
  defineProps<{
    modifiableHeaderList: HeaderList;
    isOpen?: boolean;
    disabled?: boolean;
    toolTipMessage?: string;
  }>(),
  {
    isOpen: undefined,
    toolTipMessage: undefined,
  }
);

onMounted(() => {
  if (props.isOpen) {
    onClickButton();
  }
});

const emits = defineEmits<{
  (e: 'toggle', key: string, isHide: boolean): void;
  (e: 'toggleMenu', isOpen: boolean): void;
}>();

const tableHeaderModifyMenuStyle = computed((): StyleValue => {
  return {
    top: `${tableHeaderModifyButtonPosition.value.y}px`,
    left: `${tableHeaderModifyButtonPosition.value.x}px`,
  };
});

const isMenuOpenedState = ref(false);
const buttonRef = ref<HTMLElement | null>(null);
const tableHeaderModifyButtonPosition = ref({
  x: 0,
  y: 0,
});
const tableHeaderModifyButtonHeight = ref(0);

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue === isMenuOpenedState.value) return;
    onClickButton();
    return;
  }
);

/**
 * メニュー位置を、押されたボタン自身を基準に更新する
 */
const updateMenuPosition = () => {
  const buttonContent = buttonRef.value;
  if (!buttonContent) return;

  const buttonRect = buttonContent.getBoundingClientRect();
  const menuTop = buttonRect.bottom + MENU_OFFSET_Y;
  tableHeaderModifyButtonPosition.value = {
    x: buttonRect.right - MENU_WIDTH,
    y: menuTop,
  };

  const tableContent = buttonContent.closest('.base-table-wrapper');
  if (tableContent instanceof HTMLElement) {
    const tableRect = tableContent.getBoundingClientRect();
    tableHeaderModifyButtonHeight.value = Math.max(MENU_MIN_HEIGHT, tableRect.bottom - menuTop);
    return;
  }

  tableHeaderModifyButtonHeight.value = Math.max(MENU_MIN_HEIGHT, window.innerHeight - menuTop);
};

/**
 * メニュー表示中のスクロール・リサイズに追従する
 */
const addPositionListeners = () => {
  window.addEventListener('scroll', updateMenuPosition, true);
  window.addEventListener('resize', updateMenuPosition);
};

/**
 * メニュー位置更新用のイベントリスナーを解除する
 */
const removePositionListeners = () => {
  window.removeEventListener('scroll', updateMenuPosition, true);
  window.removeEventListener('resize', updateMenuPosition);
};

/**
 * ボタンクリック時orモーダル外をクリックした際の処理
 */
const onClickButton = async () => {
  if (!isMenuOpenedState.value) {
    updateMenuPosition();
    addPositionListeners();
  } else {
    removePositionListeners();
  }
  isMenuOpenedState.value = !isMenuOpenedState.value;
  if (isMenuOpenedState.value) {
    await nextTick();
    updateMenuPosition();
  }
  emits('toggleMenu', isMenuOpenedState.value);
};

onUnmounted(() => {
  removePositionListeners();
});
</script>

<template>
  <div v-tooltip.bottom="toolTipMessage" :class="{ 'table-header-modify-button__container--disabled': disabled }">
    <button
      ref="buttonRef"
      class="table-header-modify-button__button"
      :class="{ 'table-header-modify-button__button--disabled': disabled }"
      @click="onClickButton"
    >
      <SvgLoader class="table-header-modify-button__button__icon" name="plusIconForTableHeaderModify" />
    </button>
  </div>
  <Teleport v-if="isMenuOpenedState" to="body">
    <div class="table-header-modify-button__bg" @click="onClickButton" />
    <div class="table-header-modify-button__menu" :style="tableHeaderModifyMenuStyle">
      <div class="table-header-modify-button__menu__wrapper">
        <template v-for="header in modifiableHeaderList" :key="header.key">
          <div class="table-header-modify-button__menu__button">
            <template v-if="header.isShown">
              <button @click="emits('toggle', header.key, true)">
                <SvgLoader class="table-header-modify-button__menu__icon" name="iconEyeEnabledForTableHeaderModify" />
              </button>
            </template>
            <template v-else>
              <button @click="emits('toggle', header.key, false)">
                <SvgLoader class="table-header-modify-button__menu__icon" name="iconEyeDisableForTableHeaderModify" />
              </button>
            </template>
            {{ header.text }}
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>
<style lang="scss" scoped>
.table-header-modify-button {
  &__bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 16;
  }
  &__container--disabled {
    cursor: not-allowed;
  }
  &__button {
    position: static;
    display: grid;
    align-items: center;
    justify-content: center;
    background-color: white;
    color: $textSecondary;
    width: 25px;
    height: 25px;
    border-radius: 999%;
    margin: 6px 0;
    &--disabled {
      pointer-events: none;
    }
    &__icon {
      width: min-content;
      height: min-content;
    }
  }
  &__menu {
    position: fixed;
    width: v-bind('`${MENU_WIDTH}px`');
    height: min-content;
    max-height: v-bind('`${tableHeaderModifyButtonHeight}px`');
    background-color: white;
    z-index: 17;
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
    box-shadow: 0px 0px 22px 0px #80808066;
    overflow: scroll;
    &__wrapper {
      display: grid;
      padding: 8px 0 0 8px;
      justify-items: start;
    }
    &__button {
      display: grid;
      grid-template-columns: min-content auto;
      width: 100%;
      padding: 8px 0 8px 16px;
      text-align: start;
      gap: 4px;
      align-items: center;
    }
  }
  &__divider {
    border: 1px solid $borderDefault;
  }
}
</style>
